import type { Pagination } from '@/types';
import apiSlice from './apiSlice';

export type SuspiciousIpStatus = 'Active' | 'Removed';

interface PlatformSummary {
  id: string | number;
  name: string;
}

export interface SuspiciousIpUser {
  id?: string | number;
  userId?: string | number;
  username?: string | null;
  status?: string;
  userStatus?: string;
  userRole?: string;
  role?: string;
  platformName?: string | null;
}

export interface SuspiciousIpRecord {
  id: string | number;
  ip: string;
  reason?: string | null;
  status: SuspiciousIpStatus;
  markedAt: string;
  removedAt?: string | null;
  markedBy?: { id: string | number; firstname?: string; lastname?: string; email?: string } | null;
  removedBy?: {
    id: string | number;
    firstname?: string;
    lastname?: string;
    email?: string;
  } | null;
  associatedUsersCount: number;
}

export interface SuspiciousIpAlert {
  id: string | number;
  loginAt: string;
  reviewed: boolean;
  ip: string;
  path: string;
  platformId?: string | number | null;
  user: {
    id: string | number;
    username?: string | null;
    mobile?: string | null;
    mobileNumber?: string | null;
    phoneNo?: string | number | null;
    status?: string;
    platform?: PlatformSummary | null;
  };
  suspiciousIp: { id: string | number; ip: string; reason?: string | null };
}

export interface SuspiciousIpAuditLog {
  id: string | number;
  action: string;
  ip?: string | null;
  createdAt: string;
  remark?: string | null;
  user?: { id: string | number; username: string } | null;
  actorAdmin?: {
    id: string | number;
    firstname?: string;
    lastname?: string;
    email?: string;
  } | null;
}

interface ListResponse {
  success: boolean;
  message: string;
  pagination: Pagination;
  [key: string]: unknown;
}

export interface SuspiciousIpListResponse extends ListResponse {
  suspiciousIps: SuspiciousIpRecord[];
}

export interface SuspiciousIpAlertsResponse extends ListResponse {
  alerts: SuspiciousIpAlert[];
}

export interface SuspiciousIpAuditLogsResponse extends ListResponse {
  auditLogs: SuspiciousIpAuditLog[];
}

export interface SuspiciousIpUsersResponse extends ListResponse {
  users: SuspiciousIpUser[];
}

export const suspiciousIpApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSuspiciousIps: builder.query<
      SuspiciousIpListResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        status?: SuspiciousIpStatus;
        platformId?: string;
      }
    >({
      query: ({ page, limit, search, status, platformId }) => ({
        url: '/suspicious-ip',
        method: 'GET',
        params: { page, limit, search, status, platformId, ts: Date.now() },
      }),
      providesTags: ['SuspiciousIps'],
    }),
    markSuspiciousIp: builder.mutation<
      { suspiciousIp: SuspiciousIpRecord; message: string },
      { ip: string; reason?: string; platformId?: string }
    >({
      query: (body) => ({ url: '/suspicious-ip', method: 'POST', body }),
      invalidatesTags: ['SuspiciousIps', 'SuspiciousIpAlerts', 'SuspiciousIpAuditLogs'],
    }),
    removeSuspiciousIp: builder.mutation<
      { suspiciousIp: SuspiciousIpRecord; message: string },
      string | number
    >({
      query: (id) => ({ url: `/suspicious-ip/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SuspiciousIps', 'SuspiciousIpAuditLogs'],
    }),
    getSuspiciousIpUsers: builder.query<
      SuspiciousIpUsersResponse,
      { id: string | number; page?: number; limit?: number; platformId?: string }
    >({
      query: ({ id, page, limit, platformId }) => ({
        url: `/suspicious-ip/${id}/users`,
        method: 'GET',
        params: { page, limit, platformId, ts: Date.now() },
      }),
      providesTags: ['SuspiciousIpUsers'],
    }),
    getSuspiciousIpAlerts: builder.query<
      SuspiciousIpAlertsResponse,
      { page?: number; limit?: number; reviewed?: boolean; platformId?: string }
    >({
      query: ({ page, limit, reviewed, platformId }) => ({
        url: '/suspicious-ip/alerts',
        method: 'GET',
        params: {
          page,
          limit,
          platformId,
          ...(reviewed === undefined ? {} : { reviewed: String(reviewed) }),
          ts: Date.now(),
        },
      }),
      providesTags: ['SuspiciousIpAlerts'],
    }),
    getSuspiciousIpUnreadCount: builder.query<{ count: number }, { platformId?: string } | void>({
      query: (params) => ({
        url: '/suspicious-ip/alerts/unread-count',
        method: 'GET',
        params: { platformId: params?.platformId },
      }),
      providesTags: ['SuspiciousIpAlerts'],
    }),
    reviewSuspiciousIpAlert: builder.mutation<{ alert: SuspiciousIpAlert }, string | number>({
      query: (id) => ({ url: `/suspicious-ip/alerts/${id}/review`, method: 'PATCH' }),
      invalidatesTags: ['SuspiciousIpAlerts'],
    }),
    getSuspiciousIpAuditLogs: builder.query<
      SuspiciousIpAuditLogsResponse,
      {
        page?: number;
        limit?: number;
        action?: string;
        search?: string;
        userId?: number;
        platformId?: string;
      }
    >({
      query: ({ page, limit, action, search, userId, platformId }) => ({
        url: '/suspicious-ip/audit-logs',
        method: 'GET',
        params: { page, limit, action, search, userId, platformId, ts: Date.now() },
      }),
      providesTags: ['SuspiciousIpAuditLogs'],
    }),
    changeAdminUserStatus: builder.mutation<unknown, { userId: string | number; status: string }>({
      query: ({ userId, status }) => ({
        url: `/users/admin-change-status/${userId}`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: [
        'Members',
        'UserIps',
        'SuspiciousIps',
        'SuspiciousIpUsers',
        'SuspiciousIpAuditLogs',
      ],
    }),
  }),
});

export const {
  useGetSuspiciousIpsQuery,
  useMarkSuspiciousIpMutation,
  useRemoveSuspiciousIpMutation,
  useGetSuspiciousIpUsersQuery,
  useGetSuspiciousIpAlertsQuery,
  useGetSuspiciousIpUnreadCountQuery,
  useReviewSuspiciousIpAlertMutation,
  useGetSuspiciousIpAuditLogsQuery,
  useChangeAdminUserStatusMutation,
} = suspiciousIpApi;
